import isFunction from 'lodash-es/isFunction';

import { SPLIT_TREATMENTS } from '@/constants';
import { Company } from '@/models/company';
import { CompanyPermissions, PermissionsRuleType } from '@/models/permission';
import { SplitName, SplitValue } from '@/models/split';
import { UserCompany } from '@/models/userCompany';
import { InternalUserState } from '@/piniaStores/user/user.state';
import { SeedToSaleVendors } from '@/services/api/models/traceability';

type PermissionOptions = {
  rules: PermissionsRuleType[];
  activeCompany: Company | undefined;
  integrationType: SeedToSaleVendors;
  userActiveCompanyPermissions: CompanyPermissions | null;
  user: InternalUserState;
  userActiveCompany: UserCompany | undefined;
  isSplitEnabled: (splitName: SplitName, allowedValue?: SplitValue | SplitValue[]) => boolean;
  debug?: boolean;
};

interface ValidatePermissionsReturn {
  isValid: boolean;
  failedRules: Array<{
    rule: PermissionsRuleType;
    allowedValue: unknown;
    actualValue: unknown;
  }>;
}

/**
 * Validates a list of rules against a permissions object. Permissions can
 * include feature flags, user permissions, and splits.
 *
 * Examples:
 *
 *    Given the following feature flag:
 *
 *     - 'coolFeature'
 *
 *    And given the following user permission:
 *
 *     - 'canPurchaseThngs'
 *
 *    And given the following splits:
 *
 *     - 'newDashboardSplit' with options: ['on', 'off']
 *     - 'launchGroupsSplit' with options: ['alpha', 'beta', 'theta']
 *     - 'purchasing-split' with options: ['group-a', 'group-b']
 *
 *    1) String specifying a feature flag and/or a user permission
 *
 *       `rules: ['coolFeature', 'canPurchaseThngs']`
 *
 *    2) String specifying the name of the split that must have the "on" treatment
 *
 *       `rules: ['newDashboardSplit']`
 *
 *    3) Object with one split treatment allowed
 *
 *       `rules: [{launchGroupsSplit: 'beta'}]`
 *
 *    4) Object with multiple treatments allowed
 *
 *       `rules: [{launchGroupsSplit: ['alpha', 'beta']}]`
 *
 *    5) Function
 *
 *        `rules: [({ userPermissions, isSplitEnabled }) => isSplitEnabled('purchasing-split', 'group-a') || userPermissions.canPurchaseThings]`
 *
 *    6) Combination
 *
 *       `rules: ['canPurchaseThings', 'newDashboardSplit', {launchGroupsSplit: 'beta'}]`
 *
 * @param args
 * @param args.rules - a list of required permission values
 * @param args.permissions - an object of key/value pairs that contains the active permission values
 */
export default function validatePermissions({
  rules,
  activeCompany,
  integrationType,
  userActiveCompanyPermissions,
  user,
  userActiveCompany,
  isSplitEnabled,
  debug = false,
}: PermissionOptions): ValidatePermissionsReturn {
  const flatUserPermissions = { ...user.permissions, ...user.permissions.seedToSale, seedToSale: undefined };

  delete flatUserPermissions.seedToSale;

  const allPermissions = {
    ...userActiveCompanyPermissions,
    ...user.permissions.seedToSale,
    ...user.featureFlags,
    ...user.splits,
  } as const;

  const failedRules: ValidatePermissionsReturn['failedRules'] = [];

  rules.forEach((rule) => {
    if (typeof rule === 'object') {
      const [[ruleName, allowedValue]] = Object.entries(rule);

      // ex: { launchGroupsSplit: ['alpha', 'beta'] } where ruleName is 'launchGroupsSplit' and allowedValue is ['alpha', 'beta']
      if (Array.isArray(allowedValue)) {
        const treatmentValue = Array.isArray(allPermissions[ruleName])
          ? allPermissions[ruleName][0] // See the code comments within getIsSplitEnabled for context
          : allPermissions[ruleName];

        if (!allowedValue.includes(treatmentValue)) {
          failedRules.push({
            rule,
            allowedValue,
            actualValue: treatmentValue,
          });
        }

        return;
      }

      // ex: { launchGroupsSplit: 'beta' } where ruleName is 'launchGroupsSplit' and allowedValue is 'beta'
      if (allPermissions[ruleName] !== allowedValue) {
        failedRules.push({
          rule,
          allowedValue,
          actualValue: allPermissions[ruleName],
        });

        return;
      }
    }

    if (typeof rule === 'string') {
      // check if a split is "on"
      if (typeof allPermissions[rule] === 'string' && allPermissions[rule] !== SPLIT_TREATMENTS.ON) {
        failedRules.push({
          rule,
          allowedValue: SPLIT_TREATMENTS.ON,
          actualValue: allPermissions[rule],
        });

        return;
      }

      // boolean permissions
      if (!allPermissions[rule]) {
        failedRules.push({
          rule,
          allowedValue: true,
          actualValue: allPermissions[rule],
        });
      }

      return;
    }

    if (isFunction(rule)) {
      const returnValue = rule({
        permissions: userActiveCompanyPermissions,
        user,
        userActiveCompany,
        activeCompany,
        integrationType,
        isSplitEnabled,
      });

      if (returnValue !== true) {
        failedRules.push({
          rule,
          allowedValue: true,
          actualValue: returnValue,
        });
      }
    }
  });

  if (debug) {
    // eslint-disable-next-line no-console
    console.log('Failed rules:', failedRules);
  }

  return { isValid: failedRules.length === 0, failedRules };
}
